import React, { useState } from 'react';
import { FileItem as FileItemType, ViewMode } from '../types';
import { FileItem } from './FileItem';
import { FolderItem } from './FolderItem';
import { ChevronDown } from 'lucide-react';

interface FileGridProps {
  files: FileItemType[];
  viewMode: ViewMode;
  isTrashView?: boolean;
  selectionMode?: boolean;
  selectedItems: Set<string>;
  onItemSelect: (id: string, selected: boolean) => void;
  onFolderOpen: (folderId: string) => void;
  onFileDelete?: (id: string) => void;
  onFileRestore?: (id: string) => void;
  onPermanentDelete?: (id: string) => void;
  onToggleStar?: (id: string) => void;
  onShare?: (id: string) => void;
  onDuplicate?: (id: string) => void;
  onMoveTo?: (id: string) => void;
  onRename?: (id: string) => void;
  onFilePreview: (file: FileItemType) => void;
  onFileDetails?: (file: FileItemType) => void;
  onFolderDetails?: (folder: FileItemType) => void;
  isGlobalDarkMode?: boolean;
  currentUserName?: string;
  currentUserAvatarUrl?: string | null;
  currentUserId?: string;
  isLoading?: boolean;
}

export const FileGrid: React.FC<FileGridProps> = ({
  files,
  viewMode,
  isTrashView = false,
  selectionMode = false,
  selectedItems,
  onItemSelect,
  onFolderOpen,
  onFileDelete,
  onFileRestore,
  onPermanentDelete,
  onToggleStar,
  onShare,
  onDuplicate,
  onMoveTo,
  onRename,
  onFilePreview,
  onFileDetails,
  onFolderDetails,
  isGlobalDarkMode = false,
  currentUserName,
  currentUserAvatarUrl,
  currentUserId,
  isLoading = false
}) => {
  const folders = files.filter(file => file.type === 'folder');
  const regularFiles = files.filter(file => file.type === 'file');
  const [foldersExpanded, setFoldersExpanded] = useState(true);
  const [filesExpanded, setFilesExpanded] = useState(true);

  if (isLoading) {
    return (
      <div className="flex-1 p-6">
        {/* Folders Skeleton */}
        <div className="mb-8">
          <div className="mb-4 px-2 py-2 bg-muted/30 rounded-lg w-32 h-8 animate-pulse" />
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={`folder-skeleton-${i}`} className="bg-card rounded-lg border border-border p-4 h-32 animate-pulse">
                <div className="w-12 h-12 rounded bg-muted mb-4" />
                <div className="h-4 w-3/4 bg-muted rounded" />
              </div>
            ))}
          </div>
        </div>

        {/* Files Skeleton */}
        <div>
          <div className="mb-4 px-2 py-2 bg-muted/30 rounded-lg w-32 h-8 animate-pulse" />
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
            {[...Array(12)].map((_, i) => (
              <div key={`file-skeleton-${i}`} className="bg-card rounded-lg border border-border h-48 animate-pulse">
                <div className="h-32 bg-muted rounded-t-lg" />
                <div className="p-3">
                  <div className="h-4 w-3/4 bg-muted rounded mb-2" />
                  <div className="h-3 w-1/2 bg-muted rounded" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (files.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">📁</div>
          <h3 className="text-lg font-medium text-foreground mb-2">This folder is empty</h3>
          <p className="text-muted-foreground">Upload files or create folders to get started</p>
        </div>
      </div>
    );
  }

  if (viewMode.type === 'list') {
    return (
      <div className="flex-1">
        {/* Single header row */}
        <div className="grid grid-cols-12 gap-4 px-6 py-3 bg-muted/50 border-border border-b text-sm font-medium text-muted-foreground">
          <div className="col-span-4 flex items-center gap-2">
            Name
            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
          <div className="col-span-2">Owner</div>
          <div className="col-span-3 flex items-center gap-2">
            Last modified
            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
          <div className="col-span-2">File size</div>
          <div className="col-span-1"></div>
        </div>

        {/* Combined folders and files list */}
        <div>
          {folders.map(folder => (
            <FolderItem
              key={folder.id}
              folder={folder}
              viewMode="list"
              isTrashView={isTrashView}
              selectionMode={selectionMode}
              isSelected={selectedItems.has(folder.id)}
              onSelect={onItemSelect}
              onOpen={onFolderOpen}
              onDelete={onFileDelete || (() => {})}
              onRestore={onFileRestore || (() => {})}
              onPermanentDelete={onPermanentDelete || (() => {})}
              onToggleStar={onToggleStar || (() => {})}
              onShare={onShare || (() => {})}
              onDuplicate={onDuplicate || (() => {})}
              onMoveTo={onMoveTo || (() => {})}
              onRename={onRename || (() => {})}
              onDetails={onFolderDetails || (() => {})}
              isGlobalDarkMode={isGlobalDarkMode}
              currentUserName={currentUserName}
              currentUserAvatarUrl={currentUserAvatarUrl}
              currentUserId={currentUserId}
            />
          ))}

          {regularFiles.map(file => (
            <FileItem
              key={file.id}
              file={file}
              viewMode="list"
              isTrashView={isTrashView}
              selectionMode={selectionMode}
              isSelected={selectedItems.has(file.id)}
              onSelect={onItemSelect}
              onDelete={onFileDelete || (() => {})}
              onRestore={onFileRestore || (() => {})}
              onPermanentDelete={onPermanentDelete || (() => {})}
              onToggleStar={onToggleStar || (() => {})}
              onShare={onShare || (() => {})}
              onDuplicate={onDuplicate || (() => {})}
              onMoveTo={onMoveTo || (() => {})}
              onRename={onRename || (() => {})}
              onPreview={onFilePreview || (() => {})}
              onDetails={onFileDetails || (() => {})}
              isGlobalDarkMode={isGlobalDarkMode}
              currentUserName={currentUserName}
              currentUserAvatarUrl={currentUserAvatarUrl}
              currentUserId={currentUserId}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 px-6 md:px-10 py-6">
      {/* Folders Section */}
      {folders.length > 0 && (
        <div className="mb-8">
          <div
            className="mb-4 px-2 py-2 bg-muted/30 rounded-lg cursor-pointer hover:opacity-80 transition-opacity"
            onClick={() => setFoldersExpanded(!foldersExpanded)}
          >
            <div className="flex items-center gap-2">
              <ChevronDown
                size={16}
                className={`text-foreground transition-transform duration-200 ${foldersExpanded ? '' : '-rotate-90'}`}
              />
              <h3 className="text-sm font-semibold text-foreground">Folders</h3>
            </div>
          </div>

          {foldersExpanded && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
              {folders.map(folder => (
                <FolderItem
                  key={folder.id}
                  folder={folder}
                  viewMode="grid"
                  isTrashView={isTrashView}
                  selectionMode={selectionMode}
                  isSelected={selectedItems.has(folder.id)}
                  onSelect={onItemSelect}
                  onOpen={onFolderOpen}
                  onDelete={onFileDelete || (() => {})}
                  onRestore={onFileRestore || (() => {})}
                  onPermanentDelete={onPermanentDelete || (() => {})}
                  onToggleStar={onToggleStar || (() => {})}
                  onShare={onShare || (() => {})}
                  onDuplicate={onDuplicate || (() => {})}
                  onMoveTo={onMoveTo || (() => {})}
                  onRename={onRename || (() => {})}
                  onDetails={onFolderDetails || (() => {})}
                  isGlobalDarkMode={isGlobalDarkMode}
                  currentUserName={currentUserName}
                  currentUserAvatarUrl={currentUserAvatarUrl}
                  currentUserId={currentUserId}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Files Section */}
      {regularFiles.length > 0 && (
        <div>
          <div
            className="mb-4 px-2 py-2 bg-muted/30 rounded-lg cursor-pointer hover:opacity-80 transition-opacity"
            onClick={() => setFilesExpanded(!filesExpanded)}
          >
            <div className="flex items-center gap-2">
              <ChevronDown
                size={16}
                className={`text-foreground transition-transform duration-200 ${filesExpanded ? '' : '-rotate-90'}`}
              />
              <h3 className="text-sm font-semibold text-foreground">Files</h3>
            </div>
          </div>

          {filesExpanded && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
              {regularFiles.map(file => (
                <FileItem
                  key={file.id}
                  file={file}
                  viewMode="grid"
                  isTrashView={isTrashView}
                  selectionMode={selectionMode}
                  isSelected={selectedItems.has(file.id)}
                  onSelect={onItemSelect}
                  onDelete={onFileDelete || (() => {})}
                  onRestore={onFileRestore || (() => {})}
                  onPermanentDelete={onPermanentDelete || (() => {})}
                  onToggleStar={onToggleStar || (() => {})}
                  onShare={onShare || (() => {})}
                  onDuplicate={onDuplicate || (() => {})}
                  onMoveTo={onMoveTo || (() => {})}
                  onRename={onRename || (() => {})}
                  onPreview={onFilePreview || (() => {})}
                  onDetails={onFileDetails || (() => {})}
                  isGlobalDarkMode={isGlobalDarkMode}
                  currentUserName={currentUserName}
                  currentUserAvatarUrl={currentUserAvatarUrl}
                  currentUserId={currentUserId}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};


